import clsx from 'clsx';
import { useState } from 'react';
import { NumericFormat, NumberFormatValues } from 'react-number-format';
import { v4 } from 'uuid';

export default function NumberField({
    'aria-label': ariaLabel,
    clarifier,
    className,
    'data-testid': dataTestId,
    defaultValue,
    fieldRef,
    highlight,
    info: InfoComponent,
    label,
    max,
    min,
    name,
    optional,
    placeholder,
    type,
    value,
    getAllowed,
    onChange,
    onTypeChange,
}: NumberFieldProps) {
    const [focus, setFocus] = useState(false);
    const hasValue = value !== undefined;
    let size,
        suffix,
        decimalScale = 0;
    if (type === 'percentage') {
        decimalScale = 2;
        size = (value || 1).toString().length + 4;
        suffix = '%';
        min = min || 0;
        max = max || 100;
    }

    function getIsAllowed(values: NumberFormatValues) {
        const { formattedValue, floatValue } = values;
        return (
            (!getAllowed || getAllowed(floatValue, name)) &&
            (formattedValue === '' ||
                (floatValue !== undefined &&
                    floatValue >= (min === undefined ? 0 : min) &&
                    floatValue <= (max === undefined ? Number.POSITIVE_INFINITY : max)))
        );
    }

    function handleFocus(e: any) {
        setFocus(e.type === 'focus');
    }

    function changeFocus(e: any) {
        if (e.target.tagName.toLowerCase() !== 'input') {
            e.currentTarget.closest('.field-group').querySelector('input').focus();
        }
    }

    const containerClasses = clsx(
        'd-flex flex-column-reverse field-group',
        {
            'pdf-hide': !hasValue,
            active: hasValue,
            optional,
        },
        className
    );

    const contentClasses = clsx({
        'position-absolute h-100 left-0 opacity-0 top-0 w-100': !focus && highlight && placeholder && !hasValue,
    });

    const id = `${v4()}-${name}`;

    return (
        <div className={containerClasses} data-testid={dataTestId} ref={fieldRef}>
            <div className="position-relative">
                <div className="field-control" onClick={changeFocus}>
                    <div className={contentClasses}>
                        <NumericFormat
                            aria-label={ariaLabel ?? label}
                            className="field-input"
                            decimalScale={decimalScale}
                            id={id}
                            isAllowed={getIsAllowed}
                            placeholder={highlight ? undefined : placeholder}
                            size={size}
                            suffix={suffix}
                            thousandSeparator
                            value={hasValue ? value : ''}
                            onBlur={handleFocus}
                            onFocus={handleFocus}
                            onValueChange={values => onChange(values.floatValue, name)}
                        />
                    </div>
                    {defaultValue && defaultValue === value && <div className="field-default">(Default)</div>}
                    {highlight && placeholder && !focus && !hasValue && (
                        <div className="field-placeholder">
                            <div>
                                {placeholder}
                                {clarifier && <span>{clarifier}</span>}
                            </div>
                        </div>
                    )}
                </div>
                {onTypeChange && (
                    <div className="field-types">
                        <button
                            aria-label={`${label} - Numeric`}
                            className={type === 'numeric' ? 'active' : ''}
                            type="button"
                            onClick={() => onTypeChange('numeric', name)}
                        >
                            #
                        </button>
                        <button
                            aria-label={`${label} - Percentage`}
                            className={type === 'percentage' ? 'active' : ''}
                            type="button"
                            onClick={() => onTypeChange('percentage', name)}
                        >
                            %
                        </button>
                    </div>
                )}
            </div>
            {label && (
                <div>
                    <label className="field-label" htmlFor={id}>
                        {optional && <span className="optional-label">(Optional)</span>}
                        {label}
                    </label>
                    {InfoComponent && <InfoComponent />}
                </div>
            )}
        </div>
    );
}
